import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LinkIconButton } from '@atlaskit/button/new';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import { Box, Flex, xcss } from '@atlaskit/primitives';

import { useSettingsBackButtonUtils } from './useSettingsBackButtonUtils';

const i18n = defineMessages({
	tooltip: {
		id: 'admin-side-navigation.back-button.tooltip',
		defaultMessage: 'Go back',
		description: 'Tooltip for the sidebar navigation back button',
	},
});

const TitleStyles = xcss({
	color: 'color.text.subtle',
	fontWeight: 'font.weight.bold',
	marginLeft: 'space.100',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

type SettingsBackButtonProps = {
	analyticsSource: string;
	analyticsAttributes?: Record<string, string | boolean | undefined>;
	defaultPath: string;
	title: string;
};

const ButtonStyles = xcss({
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	width: '100%',
});

// Nav 4 back button component and logic for reading saved URLs from session storage.
export const SettingsBackButton = ({
	analyticsSource,
	analyticsAttributes,
	defaultPath,
	title,
}: SettingsBackButtonProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { getRoute } = useSettingsBackButtonUtils();

	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: analyticsSource,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'sideNavigationBackButton',
				attributes: analyticsAttributes || {},
			},
		}).fire();
	}, [analyticsAttributes, analyticsSource, createAnalyticsEvent]);

	return (
		<Flex alignItems="center" xcss={ButtonStyles}>
			<LinkIconButton
				appearance="subtle"
				icon={ArrowLeftIcon}
				label={formatMessage(i18n.tooltip)}
				onClick={handleClick}
				href={getRoute(defaultPath)}
			/>
			<Box xcss={TitleStyles}>{title}</Box>
		</Flex>
	);
};
